// /hooks/useFetchBrokers.ts

import { useState, useEffect, useMemo, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { fetchQuery } from '@/utils/fetchQuery';
import { Broker } from '@/types/exchanges/single';

interface UseFetchBrokersProps {
    categories?: 'foreign' | 'iranian' | 'props' | 'broker' | 'gold' | undefined;
    searchTerm: string;
}

interface PageInfo {
    endCursor: string;
    hasMore: boolean;
}

// Module-level cache across component mounts / modal open-closes
const platformsCache: Record<string, Broker[]> = {};

const useFetchBrokers = (
    { categories = 'broker', searchTerm = '' }: UseFetchBrokersProps,
    options: { enabled: boolean } = { enabled: true },
) => {
    const categoryKey = categories || 'broker';
    const [brokers, setBrokers] = useState<Broker[]>(() => platformsCache[categoryKey] || []);
    const [pageInfo, setPageInfo] = useState<PageInfo>({
        endCursor: '',
        hasMore: true,
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const { enabled } = options;

    const fetchBrokers = useMemo(
        () =>
            debounce(async (searchQuery: string, cursor: string = '') => {
                // Only show loading if we don't already have cached items
                if (!platformsCache[categoryKey] || cursor !== '') {
                    setIsLoading(true);
                }
                setError(null);
                try {
                    let query = '';
                    const variables: Record<string, any> = {
                        first: 50,
                    };
                    if (searchQuery && searchQuery.trim()) {
                        variables.search = searchQuery.trim();
                    }
                    if (cursor) {
                        variables.after = cursor;
                    }

                    // 1. Query selection
                    if (categories === 'broker') {
                        query = `query Brokers($search: String, $first: Int!, $after: String) {
                            brokers(where: { search: $search, orderby: { field: BROKER_FINAL_RATING, order: DESC }}, first: $first, after: $after) {
                                nodes { brokerSingle { newfields { logo { sourceUrl } faName enName badge sponsored verified } } uri }
                                pageInfo { endCursor hasNextPage offsetPagination { hasMore } }
                            }
                        }`;
                    } else if (categories === 'gold') {
                        query = `query goldPlatforms($search: String, $first: Int!, $after: String) {
                            goldPlatforms(where: { search: $search, orderby: { field: DATE, order: DESC }}, first: $first, after: $after) {
                                nodes { brokerSingle { newfields { logo { sourceUrl } faName enName badge sponsored verified } } uri }
                                pageInfo { endCursor hasNextPage offsetPagination { hasMore } }
                            }
                        }`;
                    } else if (categories === 'props') {
                        query = `query exchange($search: String, $first: Int!, $after: String) {
                            props(where: { search: $search, orderby: { field: BROKER_FINAL_RATING, order: DESC }}, first: $first, after: $after) {
                                nodes { brokerSingle { newfields { logo { sourceUrl } faName enName badge sponsored verified } } uri }
                                pageInfo { endCursor hasNextPage offsetPagination { hasMore } }
                            }
                        }`;
                    } else if (categories === 'foreign' || categories === 'iranian') {
                        query = `query exchange($search: String, $first: Int!, $after: String) {
                            allExchange(where: { search: $search, orderby: { field: BROKER_FINAL_RATING, order: DESC }, taxQuery: { taxArray: [{ taxonomy: EXCHANGECATEGORY, terms: ["${categories}"], field: SLUG, operator: IN }] } }, first: $first, after: $after) {
                                nodes { brokerSingle { newfields { logo { sourceUrl } faName enName badge sponsored verified } } uri }
                                pageInfo { endCursor hasNextPage offsetPagination { hasMore } }
                            }
                        }`;
                    }

                    if (!query) {
                        setBrokers([]);
                        setIsLoading(false);
                        return;
                    }

                    const data = await fetchQuery<any>(query, variables);

                    // 2. Optimized Data Extraction
                    const resultSource =
                        data?.brokers || data?.goldPlatforms || data?.props || data?.allExchange;

                    const nodes = resultSource?.nodes || [];
                    const fetchedBrokers: Broker[] = nodes.map((node: any) => ({
                        faName: node.brokerSingle?.newfields?.faName || '',
                        enName: node.brokerSingle?.newfields?.enName || '',
                        logo: node.brokerSingle?.newfields?.logo,
                        uri: node.uri || '',
                        badge: node.brokerSingle?.newfields?.badge,
                        sponsored: node.brokerSingle?.newfields?.sponsored,
                        verified: node.brokerSingle?.newfields?.verified,
                    }));

                    // 3. Update State & Cache
                    if (cursor === '') {
                        platformsCache[categoryKey] = fetchedBrokers;
                        setBrokers(fetchedBrokers);
                    } else {
                        setBrokers((prev) => [...prev, ...fetchedBrokers]);
                    }

                    // 4. Update Pagination
                    const hasMore = Boolean(
                        resultSource?.pageInfo?.offsetPagination?.hasMore ??
                        resultSource?.pageInfo?.hasNextPage ??
                        nodes.length >= 50,
                    );
                    setPageInfo({
                        endCursor: resultSource?.pageInfo?.endCursor || '',
                        hasMore,
                    });
                } catch (err) {
                    console.error('Failed to fetch brokers:', err);
                    setError('خطا در دریافت اطلاعات.');
                    setPageInfo((prev) => ({ ...prev, hasMore: false }));
                } finally {
                    setIsLoading(false);
                }
            }, 200),
        [categories, categoryKey],
    );

    const fetchMoreBrokers = useCallback(() => {
        if (!enabled || isLoading || !pageInfo.hasMore) return;
        fetchBrokers(searchTerm, pageInfo.endCursor);
    }, [enabled, isLoading, pageInfo, fetchBrokers, searchTerm]);

    useEffect(() => {
        if (enabled) {
            // If we already have cache for this category, load it immediately
            if (platformsCache[categoryKey]?.length) {
                setBrokers(platformsCache[categoryKey]);
            }
            fetchBrokers(searchTerm, '');
        }
    }, [searchTerm, categories, categoryKey, fetchBrokers, enabled]);

    return { brokers, isLoading, error, pageInfo, fetchMoreBrokers };
};

export default useFetchBrokers;
